import { useState, useEffect, forwardRef } from 'react'
import { Close } from '@mui/icons-material'
import { SocketConnection } from '../connections'
import { reportsRoutes, usersRoutes } from '../routes'
import { useAuth, useReportsContext, useSitesContext, useUsersContext, useUser, useStatus, useAlertContext } from '../context'
import { Button, Dialog, Fab, FormControl, InputLabel, MenuItem, Select, Slide, Toolbar, Grid, Autocomplete, Checkbox, TextField, Chip } from '@mui/material'
import { ROLES } from '../config'

const Transition = forwardRef(function Transition (props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
})

const AssignDialog = ({ open, report, closeModal, reportType, onlyClose }) => {
    const { userData } = useAuth()
    const { admin } = useUser()
    const { setstatus } = useStatus()
    const { setConfirm } = useAlertContext()
    const { users, usersMap } = useUsersContext()
    const { sites } = useSitesContext()
    const { saveReport, refreshNumbers, getReports, removeReportFromReportsToShow, assignReport, reloadCurrentPage, updateNumbersLocally, valueFilter, updateLocalReport } = useReportsContext()
    const [operarios, setOperarios] = useState([])
    const [colorState, setColorState] = useState()
    const [localReport, setLocalReport] = useState(report)
    const { newIdIndex, idIndex, guide, state, usersAssigned, site, obra } = localReport || {}

    useEffect(() => {
        if (open) {
            setLocalReport(report)
        }
    }, [report, open])
    const [stateAssignment, setStateAssignment] = useState(false)
    const [data, setData] = useState('')
    const [closeType, setCloseType] = useState(false)
    const [userAssigned, setUserAssigned] = useState()
    const [reading, setReading] = useState(true)
    const [siteName, setSiteName] = useState('')
    const [removeUserButton, setRemoveUserButton] = useState(true)
    const [selectedUsersToAssign, setSelectedUsersToAssign] = useState([])

    useEffect(() => {
        if (usersAssigned && usersAssigned.length > 0) {
            setRemoveUserButton(false)
        } else {
            setRemoveUserButton(true)
        }
    }, [usersAssigned])

    useEffect(() => {
        if (open) {
            console.log('AssignDialog: Dialog opened.')
            setSiteNameById()
            setReading(true)

            const roleToFetch = reportType === 'Inspección' ? ROLES.INSPECTION_WORKER : ROLES.MAINTENANCE_OPERATOR

            let filteredUsers = users.filter(user => {
                if (!user.role) return false
                const dbName = user.role.dbName

                const isBaseAllowed = (
                    dbName === roleToFetch ||
                    dbName === ROLES.OPERATOR
                )
                const isOIAllowed = (dbName === ROLES.OPERATOR_INSPECTOR || dbName === ROLES.OPERATOR_INSPECTOR_UNIVERSAL) && reportType === 'Inspección'

                return isBaseAllowed || isOIAllowed
            })

            if (obra && obra._id) {
                filteredUsers = filteredUsers.filter(user =>
                    user.obras && user.obras.some(o => (o._id || o) === obra._id)
                )
            } else if (site) {
                filteredUsers = filteredUsers.filter(user =>
                    user.obras && user.obras.some(o => o.idObra === site)
                )
            }

            const sortedUsers = filteredUsers.sort((a, b) => {
                if (a.name < b.name) return -1
                if (a.name > b.name) return 1
                return 0
            })

            setOperarios(sortedUsers)
            setReading(false)
        }
    }, [open, reportType, users, site])

    useEffect(() => {
        if (operarios && operarios.length > 0) {
            setReading(false)
        }
    }, [operarios])

    const setSiteNameById = () => {
        const siteFound = sites.find(siteToFilter => site === siteToFilter.idobra)
        if (siteFound) {
            setSiteName(siteFound.descripcion)
        } else {
            setSiteName('Obra no encontrada')
        }
    }

    const handleAssignUsers = async () => {
        if (selectedUsersToAssign.length === 0) {
            setstatus({
                status: 'error',
                title: 'Error de Selección',
                message: 'Debe seleccionar al menos un operario para asignar.',
                severity: 'warning'
            })
            return
        }

        const alreadyAssignedUsers = selectedUsersToAssign.filter(user =>
            (localReport.usersAssigned || []).some(assigned => (assigned._id || assigned) === user._id)
        )

        if (alreadyAssignedUsers.length > 0) {
            const names = alreadyAssignedUsers.map(u => `${u.name} ${u.lastName}`).join(', ')
            setstatus({
                status: 'error',
                title: 'Usuarios ya asignados',
                message: `Los siguientes usuarios ya están asignados a esta OT: ${names}`,
                severity: 'warning'
            })
            return
        }

        const userIdsToAssign = selectedUsersToAssign.map(user => user._id)
        const userNames = selectedUsersToAssign.map(user => `${user.name} ${user.lastName}`).join(', ')

        setConfirm(
            'Confirmar Asignación',
            `Confirme que asignará a ${userNames} a la OT ${localReport.newIdIndex || localReport.idIndex}`,
            async () => {
                const assignmentData = {
                    userIds: userIdsToAssign
                }

                try {
                    console.log('Attempting to assign report:', localReport._id, 'with data:', assignmentData)

                    const isSearching = !!(valueFilter && valueFilter.trim() !== '')

                    if (!isSearching && (localReport.state === 'Asignar' || localReport.state === 'Reasignar')) {
                        removeReportFromReportsToShow(localReport._id)
                        updateNumbersLocally(reportType, localReport.state, 'En proceso')
                    }

                    close()

                    setstatus({
                        status: 'generating',
                        title: 'Asignando OT',
                        message: `Asignando OT ${localReport.newIdIndex || localReport.idIndex} a ${userNames}...`,
                        severity: 'info'
                    })

                    const response = await assignReport(localReport._id, assignmentData)
                    console.log('Report assignment successful. Response:', response)

                    if (isSearching) {
                        const updatedData = response.data || {}
                        await updateLocalReport(localReport._id, {
                            usersAssigned: updatedData.usersAssigned || selectedUsersToAssign,
                            state: updatedData.state || 'En proceso',
                            level: updatedData.level ?? localReport.level
                        })
                    } else {
                        reloadCurrentPage()
                    }

                    const isCurrentUserAssigned = userIdsToAssign.includes(userData._id || userData.id)
                    if (isCurrentUserAssigned) {
                        getReports()
                    }

                    setstatus({
                        status: 'completed',
                        title: 'OT Asignada',
                        message: `La OT ${localReport.newIdIndex || localReport.idIndex} ha sido asignada correctamente.`,
                        severity: 'success'
                    })

                    selectedUsersToAssign.forEach(user => {
                        SocketConnection.sendnotificationToUser(
                            `${userData._id}`,
                            user._id,
                            'Asignaciones',
                            'Se ha asignado nueva OT',
                            `OT ${localReport.newIdIndex || localReport.idIndex} asignada a usted`,
                            '/assignment',
                            localReport._id
                        )
                    })
                } catch (error) {
                    console.error('Error al asignar reporte:', error)
                    setstatus({
                        status: 'error',
                        title: 'Error de Asignación',
                        message: `No se pudo asignar la OT ${localReport.newIdIndex || localReport.idIndex}.`,
                        severity: 'error',
                        errorDetails: {
                            code: error.response?.status || 'Error',
                            info: error.response?.data?.message || error.message
                        }
                    })
                }
            }
        )
    }

    const handleDeleteUser = async (userIdToDelete) => {
        const userToDelete = usersMap[userIdToDelete]
        if (!userToDelete) return

        setConfirm(
            'Confirmar Desasignación',
            `¿Confirma que desea quitar a ${userToDelete.name} ${userToDelete.lastName} de la OT ${localReport.newIdIndex || localReport.idIndex}?`,
            async () => {
                const userIdsToKeep = (localReport.usersAssigned || [])
                    .map(u => u._id || u)
                    .filter(id => id !== userIdToDelete)

                const newState = userIdsToKeep.length === 0 ? 'Reasignar' : localReport.state

                const reportUpdatePayload = {
                    _id: localReport._id,
                    usersAssigned: userIdsToKeep,
                    state: newState
                }

                try {
                    setstatus({
                        status: 'generating',
                        title: 'Desasignando',
                        message: `Quitando a ${userToDelete.name} ${userToDelete.lastName} de la OT ${localReport.newIdIndex || localReport.idIndex}...`,
                        severity: 'info'
                    })

                    await saveReport(reportUpdatePayload)

                    const isSearching = !!(valueFilter && valueFilter.trim() !== '')

                    if (!isSearching && (newState !== localReport.state)) {
                        removeReportFromReportsToShow(localReport._id)
                        updateNumbersLocally(reportType, localReport.state, newState === 'Reasignar' ? 'Asignar' : newState)
                    }

                    if (isSearching) {
                        await updateLocalReport(localReport._id, {
                            usersAssigned: userIdsToKeep,
                            state: newState
                        })
                    } else {
                        reloadCurrentPage()
                    }

                    setLocalReport(prev => ({
                        ...prev,
                        usersAssigned: prev.usersAssigned.filter(u => (u._id || u) !== userIdToDelete),
                        state: newState
                    }))

                    if (userIdToDelete === (userData._id || userData.id)) {
                        getReports()
                    }

                    setstatus({
                        status: 'completed',
                        title: 'Usuario desasignado',
                        message: `El usuario ${userToDelete.name} ${userToDelete.lastName} ha sido quitado de la OT.`,
                        severity: 'success'
                    })

                    SocketConnection.sendnotificationToUser(
                        'reporte-desasignado',
                        `${userData._id}`,
                        userIdToDelete,
                        'Asignaciones',
                        'Se ha desasignado OT',
                        `OT ${localReport.newIdIndex || localReport.idIndex} fue desasignada`,
                        '/assignment',
                        localReport._id
                    )
                } catch (error) {
                    console.error('Error al desasignar reporte:', error)
                    setstatus({
                        status: 'error',
                        title: 'Error de Desasignación',
                        message: 'Hubo un error al desasignar el usuario. Por favor, intente nuevamente.',
                        severity: 'error',
                        errorDetails: {
                            info: error.message
                        }
                    })
                }
            }
        )
    }

    const close = () => {
        if (closeType) {
            closeModal()
        } else {
            onlyClose()
        }
    }

    useEffect(() => {
        setUserAssigned(usersAssigned[0])
        setCloseType(false)
        if (state === 'Asignar') {
            setColorState('#DE4343')
        } else if (state === 'En proceso') {
            setColorState('#F2994A')
        } else if (state === 'Por cerrar') {
            setColorState('#F2C94C')
        } else if (state === 'Completadas') {
            setColorState('#27AE60')
        }
    }, [guide, state])

    return (
        <Dialog
            open={open}
            onClose={close}
            TransitionComponent={Transition}
            adaptiveHeight={true}
        >
            <div style={{ padding: 30, minWidth: 500 }} >
                <Toolbar style={{ width: '100%', height: 70, paddingLeft: 0 }}>
                    <h2>Asignar Pauta de <br /> {reportType}: {guide}</h2>
                    <div style={{ position: 'absolute', right: 0, backgroundColor: colorState, paddingTop: 3, borderRadius: 5, width: 100, height: 20, textAlign: 'center' }}>
                        <p style={{ margin: 0, fontSize: 12 }}>{state.toUpperCase()}</p>
                    </div>
                </Toolbar>
                <div style={{ width: '100%', height: 90 }}>
                    <div style={{ width: 'calc(100%/3)', float: 'left', display: 'block', margin: 0 }}>
                        <strong>Faena:</strong> <br />
                        {obra.descripcion || 'Sitio no encontrado'}
                    </div>
                    <div style={{ width: 'calc(100%/3)', float: 'left' }}>
                        <strong>Número de orden:</strong> <br />
                        {newIdIndex || idIndex}
                    </div>
                    <div style={{ width: 'calc(100%/3)', float: 'left', display: 'block', margin: 0 }}>
                        <strong>Pauta:</strong> <br />
                        {guide}
                    </div>
                </div>
                <div style={{ width: '100%', height: 59 }}>
                    {stateAssignment && <div style={{ width: 'calc(100%/3)', float: 'left' }}>
                        <strong>{data}</strong> <br />
                    </div>}
                </div>
                <FormControl fullWidth>
                    <Autocomplete
                        multiple
                        options={operarios}
                        disableCloseOnSelect
                        getOptionLabel={(option) => `${option.name} ${option.lastName}`}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        value={selectedUsersToAssign}
                        onChange={(event, newValue) => {
                            setSelectedUsersToAssign(newValue)
                        }}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                                {`${option.name} ${option.lastName}`}
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} label="Seleccionar operarios" placeholder="Operarios" />
                        )}
                        disabled={reading}
                        sx={{ mt: 2 }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAssignUsers}
                        disabled={selectedUsersToAssign.length === 0}
                        sx={{ mt: 2, mb: 2 }}
                    >
                        Asignar seleccionados
                    </Button>
                    {localReport?.usersAssigned?.length > 0 && (
                        <div style={{ marginTop: 20 }}>
                            <p style={{ margin: 0 }}><strong>Usuarios asignados:</strong></p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, maxHeight: 100, overflowY: 'auto' }}>
                                {localReport.usersAssigned.map((u, i) => {
                                    const userObj = typeof u === 'object' ? u : usersMap[u]
                                    return userObj ? (
                                        <Chip
                                            key={userObj._id || i}
                                            label={`${userObj.name} ${userObj.lastName}`}
                                            variant="outlined"
                                            onDelete={() => handleDeleteUser(userObj._id)}
                                        />
                                    ) : null
                                })}
                            </div>
                        </div>
                    )}
                </FormControl>
                <Fab onClick={close} style={{ position: 'absolute', right: 10, top: 10, boxShadow: 'none', backgroundColor: 'transparent' }}>
                    <Close style={{ color: '#ccc' }} />
                </Fab>
            </div>
        </Dialog>
    )
}

export default AssignDialog
